import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/service";
import API_ROUTES from "../../api-routes";
import { isValidRedirectUrl } from "../../utils/urlValidation";

const initialState = {
  isLoading: true,
  email: null,
  firstName: null,
  lastName: null,
  role: null,
  isAdmin: false,
  id: null,
  entityIds: [],
  timezone: "",
  errorMessage: null,
  errorStatus: null,
  config: null,
  access: [],
  subscriber: null,
  unreadNotificationCount: 0,
};

export const getLoggedInUser = createAsyncThunk(
  "login/getLoggedInUser",
  async (token, thunkAPI) => {
    try {
      const res = await apiService(API_ROUTES.VERIFY_TOKEN, "GET", { token });
      return res;
    } catch (e) {
      return thunkAPI.rejectWithValue({
        message: e.message,
        status: e.status,
      });
    }
  },
);

export const fetchUserDetails = createAsyncThunk(
  "login/fetchUserDetails",
  async (_, thunkAPI) => {
    try {
      return await apiService(API_ROUTES.FETCH_USER, "GET");
    } catch (e) {
      return thunkAPI.rejectWithValue({
        message: e.message,
        status: e.status,
        statusText: e.statusText,
      });
    }
  },
);

export const LoggedOut = createAsyncThunk(
  "login/LoggedOut",
  async (_, thunkAPI) => {
    try {
      const { data } = await apiService(API_ROUTES.LOGOUT, "GET");
      try {
        await fetch(data);
      } finally {
        window.location.reload();
      }
    } catch (e) {
      return thunkAPI.rejectWithValue({
        message: e.message,
        status: e.status,
      });
    }
  },
);

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    updateConfig: (state, action) => {
      state.config = action.payload;
    },
    updateUnreadNotification: (state, action) => {
      state.unreadNotificationCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLoggedInUser.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null; // clear previous error
        state.errorStatus = null;
      })
      .addCase(getLoggedInUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.firstName = payload.data.first_name;
        state.lastName = payload.data.last_name;
        state.email = payload.data.email;

        state.id = payload.data.id;
        state.entityIds = payload.data.entityIds;
        state.role = payload.data.role;
        state.errorMessage = null;
        state.errorStatus = null;
        state.config = payload.data.config;
      })
      .addCase(getLoggedInUser.rejected, (state, action) => {
        state.isLoading = false;
        // Extract error information from the payload
        const errorData = action.payload || {};
        state.errorStatus = errorData.status;

        // a fallback message if none came from the server
        state.errorMessage =
          errorData.message ||
          "We could not retrieve login information at this time";

        throw new Error("We could not retrieve login information at this time");
      })

      // fetchUserDetails
      .addCase(fetchUserDetails.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
        state.errorStatus = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.firstName = payload.data.first_name;
        state.lastName = payload.data.last_name;
        state.email = payload.data.email;
        state.access = payload.data.access;
        state.timezone_offset_gmt = payload.data.timezone_offset_gmt;
        state.timezone = payload.data.timezone;
        state.id = payload.data.id;
        state.isAdmin = payload.data.role === "GXM_ADMIN";
        state.role = payload.data.role;
        state.entityIds = payload.data.entityIds;
        state.errorMessage = null;
        state.errorStatus = null;
        state.config = payload.data.config;
        state.subscriber = payload.data.novuSubscriberId;
        state.lsp_id = payload?.data?.lsp_id;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.isLoading = false;

        // Extract error information from the payload
        const errorData = action.payload || {};
        state.errorStatus = errorData.status;

        // Set specific error messages based on status code
        if (errorData.status === 0) {
          state.errorMessage =
            "Server connection error. The server is currently unavailable. Please try again later.";
        } else if (errorData.status === 403) {
          state.errorMessage =
            "You don't have permission to access this application. Please contact your administrator if you believe this is a mistake.";
        } else if (errorData.status === 500) {
          state.errorMessage =
            "Internal server error. Our team has been notified and is working to resolve the issue.";
        } else if (
          errorData.status === 502 ||
          errorData.status === 503 ||
          errorData.status === 504
        ) {
          state.errorMessage =
            "Server unavailable. The service is temporarily unavailable. Please try again later.";
        } else {
          state.errorMessage =
            errorData.message ||
            "Sorry, but we could not retrieve user details at this time";
        }
      })
      // LoggedOut
      .addCase(LoggedOut.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(LoggedOut.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        const redirectUrl = payload.data;

        if (isValidRedirectUrl(redirectUrl)) {
          window.location.href = redirectUrl;
        } else {
          console.error(
            "Invalid redirect URL provided for logout:",
            redirectUrl,
          );
        }
      })
      .addCase(LoggedOut.rejected, (state, action) => {
        state.isLoading = false;
        const errorData = action.payload || {};
        state.errorStatus = errorData.status;
        state.errorMessage =
          errorData.message || "We could not log you out at this time";
      });
  },
});
export const { updateConfig, updateUnreadNotification } = loginSlice.actions;

export default loginSlice.reducer;
